/**
 * Reservations Hooks
 *
 * TanStack Query hooks for customer reservations operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getReservations,
    getReservationById,
    cancelReservation,
    getReservationInvoice,
    requestExtension,
    downloadInvoiceAsPdf,
    updateReservationSchedule,
} from '@/api/reservations';
import {
    CustomerReservationsRequest,
    CustomerCancelReservationRequest,
    CustomerExtensionRequest,
    UpdateReservationScheduleRequest,
} from '@/types/reservations';

export const reservationKeys = {
    all: ['reservations'] as const,
    lists: () => [...reservationKeys.all, 'list'] as const,
    list: (params: CustomerReservationsRequest) =>
        [...reservationKeys.lists(), params] as const,
    summary: () => [...reservationKeys.all, 'summary'] as const,
    details: () => [...reservationKeys.all, 'detail'] as const,
    detail: (id: string) => [...reservationKeys.details(), id] as const,
    invoices: () => [...reservationKeys.all, 'invoice'] as const,
    invoice: (id: string) => [...reservationKeys.invoices(), id] as const,
};

/**
 * Hook to fetch customer reservations list
 */
export const useReservations = (params: CustomerReservationsRequest = {}) => {
    return useQuery({
        queryKey: reservationKeys.list(params),
        queryFn: () => getReservations(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
};

/**
 * Hook to fetch a combined count of current reservations.
 * Current reservations are Pending, Confirmed, and Active.
 */
export const useReservationSummary = () => {
    return useQuery({
        queryKey: reservationKeys.summary(),
        queryFn: async () => {
            const currentStatuses: NonNullable<CustomerReservationsRequest['status']>[] = [
                'Pending',
                'Confirmed',
                'Active',
            ];

            const results = await Promise.all(
                currentStatuses.map((status) =>
                    getReservations({
                        status,
                        page: 1,
                        pageSize: 1,
                    })
                )
            );

            return results.reduce((total, result) => total + result.totalCount, 0);
        },
        staleTime: 2 * 60 * 1000,
    });
};

/**
 * Hook to fetch a single reservation by ID
 */
export const useReservation = (reservationId: string) => {
    return useQuery({
        queryKey: reservationKeys.detail(reservationId),
        queryFn: () => getReservationById(reservationId),
        enabled: !!reservationId,
        staleTime: 1 * 60 * 1000, // 1 minute
    });
};

/**
 * Hook to cancel a reservation
 * Returns cancellation result including refund information
 */
export const useCancelReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            reservationId,
            request,
        }: {
            reservationId: string;
            request: CustomerCancelReservationRequest;
        }) => cancelReservation(reservationId, request),
        onSuccess: (result, { reservationId }) => {
            // Invalidate the detail cache to show cancelled state
            queryClient.invalidateQueries({
                queryKey: reservationKeys.detail(reservationId),
            });
            // Invalidate list to refresh
            queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: reservationKeys.summary() });
            return result;
        },
    });
};

/**
 * Hook to fetch reservation invoice
 */
export const useReservationInvoice = (reservationId: string) => {
    return useQuery({
        queryKey: reservationKeys.invoice(reservationId),
        queryFn: () => getReservationInvoice(reservationId),
        enabled: !!reservationId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to request an extension for a reservation
 */
export const useRequestExtension = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            reservationId,
            request,
        }: {
            reservationId: string;
            request: CustomerExtensionRequest;
        }) => requestExtension(reservationId, request),
        onSuccess: (result, { reservationId }) => {
            // Invalidate the detail cache to refetch with updated data
            queryClient.invalidateQueries({
                queryKey: reservationKeys.detail(reservationId),
            });
            queryClient.invalidateQueries({
                queryKey: reservationKeys.invoice(reservationId),
            });
            // Also invalidate list in case status changed
            queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: reservationKeys.summary() });
            return result;
        },
    });
};

/**
 * Hook to download invoice as PDF
 */
export const useDownloadInvoice = () => {
    return useMutation({
        mutationFn: ({
            reservationId,
            reservationNumber,
        }: {
            reservationId: string;
            reservationNumber: string;
        }) => downloadInvoiceAsPdf(reservationId, reservationNumber),
    });
};

export const usePrintableInvoice = (reservationId: string) => {
    const reservationQuery = useReservation(reservationId);
    const invoiceQuery = useReservationInvoice(reservationId);

    return {
        reservationQuery,
        invoiceQuery,
    };
};

/**
 * Hook to update the pickup time and/or return datetime for a reservation
 */
export const useUpdateSchedule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            reservationId,
            request,
        }: {
            reservationId: string;
            request: UpdateReservationScheduleRequest;
        }) => updateReservationSchedule(reservationId, request),
        onSuccess: (_data, { reservationId }) => {
            queryClient.invalidateQueries({
                queryKey: reservationKeys.detail(reservationId),
            });
            queryClient.invalidateQueries({
                queryKey: reservationKeys.invoice(reservationId),
            });
            queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: reservationKeys.summary() });
        },
    });
};
